import React, { useImperativeHandle, forwardRef } from 'react';
import { MasterFlightRecord } from '../../../../../utils/types';
import { useFormValidation } from './hooks';
import {
  FlightInformationSection,
  SchedulePeriodSection,
} from './sections';
import { ParentFlightSelector } from './sections/ParentFlightSelector';

interface BasicInfoTabProps {
  draft: MasterFlightRecord;
  updateDraft: <K extends keyof MasterFlightRecord>(key: K, value: MasterFlightRecord[K]) => void;
  setDraft: React.Dispatch<React.SetStateAction<MasterFlightRecord>>;
  editMode: boolean;
  isVariantMode: boolean;
  isAdhocMode: boolean;
  parentDateRange: { startDate: string; endDate: string } | null;
  setParentDateRange: React.Dispatch<React.SetStateAction<{ startDate: string; endDate: string } | null>>;
}

export interface BasicInfoTabRef {
  validateAllFields: () => boolean; // Returns true if there are errors
  resetValidation: () => void;
}

/**
 * BasicInfoTab - Simplified and Modularized
 *
 * Uses modular sections:
 * - FlightInformationSection: Flight Owner, Number, Suffix, Service Type (Variant ID auto-generated)
 * - SchedulePeriodSection: Dates, Season ID, Frequency
 *
 * Note: Codeshare information is now managed per sector in the Stations Tab
 *
 * Shared logic extracted to custom hooks in ./hooks/
 */
export const BasicInfoTab = forwardRef<BasicInfoTabRef, BasicInfoTabProps>(
  ({ draft, updateDraft, setDraft, editMode, isVariantMode, isAdhocMode, parentDateRange, setParentDateRange }, ref) => {
    const {
      handleBlur: handleFieldBlur,
      getFieldError,
      revalidateIfTouched,
      validateAllFields: validateAll,
      resetValidation: reset,
    } = useFormValidation();

    // Expose validation methods to parent via ref
    useImperativeHandle(ref, () => ({
      validateAllFields: () => validateAll(draft),
      resetValidation: () => reset(),
    }));

    // Wrapper functions to pass draft to validation hooks
    const handleBlur = (fieldName: keyof MasterFlightRecord) => {
      handleFieldBlur(fieldName, draft[fieldName], draft);
    };

    const revalidate = (fieldName: keyof MasterFlightRecord, value: MasterFlightRecord[keyof MasterFlightRecord]) => {
      revalidateIfTouched(fieldName, value, draft);
    };

    return (
      <div className="space-y-4">
        {/* Parent Flight Selector — only in adhoc mode */}
        {isAdhocMode && (
          <ParentFlightSelector
            draft={draft}
            setDraft={setDraft}
            onParentSelect={(parent: MasterFlightRecord) => {
              setParentDateRange({ startDate: parent.startDate, endDate: parent.endDate });
            }}
          />
        )}

        {/* Flight Information Section */}
        <FlightInformationSection
          draft={draft}
          updateDraft={updateDraft}
          setDraft={setDraft}
          editMode={editMode}
          isVariantMode={isVariantMode}
          isAdhocMode={isAdhocMode}
          getFieldError={getFieldError}
          handleBlur={handleBlur}
          revalidateIfTouched={revalidate}
        />

        {/* Schedule Period Section */}
        <SchedulePeriodSection
          draft={draft}
          updateDraft={updateDraft}
          setDraft={setDraft}
          isVariantMode={isVariantMode}
          isAdhocMode={isAdhocMode}
          parentDateRange={parentDateRange}
          getFieldError={getFieldError}
          handleBlur={handleBlur}
          revalidateIfTouched={revalidate}
        />
      </div>
    );
  }
);

BasicInfoTab.displayName = 'BasicInfoTab';
